import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Users, School, ChevronRight, Shuffle, RefreshCw, Bookmark, Volume2, Pause, Play } from 'lucide-react';
import { getStructure, getRandomItem } from '../services/api';
import { useAudioPlayer } from '../hooks/useAudioPlayer';
import './Home.css';

const Home = () => {
  const [structure, setStructure] = useState(null);
  const [currentItem, setCurrentItem] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const { isPlaying, isPaused, playAudio, cleanupAudio } = useAudioPlayer();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [structureData, randomData] = await Promise.all([
          getStructure(),
          getRandomItem(),
        ]);
        setStructure(structureData);
        setCurrentItem(randomData);
        // 初始化历史记录
        setHistory([randomData]);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setPageLoading(false);
      }
    };
    fetchData();
  }, []);

  // 获取随机条目
  const fetchRandomItem = useCallback(async () => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    setLoading(true);
    
    try {
      const data = await getRandomItem();
      
      // 添加到历史记录
      const newHistory = [...history, data];
      
      // 限制历史记录长度
      if (newHistory.length > 50) {
        newHistory.shift();
      }
      
      setHistory(newHistory);
      
      // 延迟显示，创造动画效果
      setTimeout(() => {
        setCurrentItem(data);
        setIsAnimating(false);
        setLoading(false);
      }, 300);
      
    } catch (error) {
      console.error('Error fetching random item:', error);
      setIsAnimating(false);
      setLoading(false);
    }
  }, [history, isAnimating]);



  // 处理播放按钮点击
  const handlePlayAudio = () => {
    if (currentItem) {
      playAudio(currentItem.content);
    }
  };

  // 切换条目时停止播放
  useEffect(() => {
    cleanupAudio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentItem]);

  // 获取按钮图标和标题
  const getAudioButtonContent = () => {
    if (isPlaying && !isPaused) {
      return { icon: <Pause size={18} />, title: '暂停播放' };
    } else if (isPlaying && isPaused) {
      return { icon: <Play size={18} />, title: '继续播放' };
    } else {
      return { icon: <Volume2 size={18} />, title: '播放语音' };
    }
  };

  if (pageLoading) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
        <p>加载中...</p>
      </div>
    );
  }

  const features = [
    {
      id: 'student_rules',
      icon: BookOpen,
      title: '学训一百条',
      subtitle: '德 · 勤 · 能 · 绩 · 处人 · 处事',
      description: '为学生成长成才指引方向，培养德才兼备的优秀人才',
      color: '#c41230',
      count: 100,
    },
    {
      id: 'teacher_rules',
      icon: Users,
      title: '师训两百条',
      subtitle: '为人处事 · 治校办学 · 教书育人',
      description: '为教师职业发展和教育教学提供指导',
      color: '#1a5f7a',
      count: 200,
    },
    {
      id: 'school_management',
      icon: School,
      title: '治校办学三百条',
      subtitle: '学院文化 · 领导艺术 · 办学方略',
      description: '为学院管理和可持续发展提供战略指引',
      color: '#d4af37',
      count: 300,
    },
  ];

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <h1 className="hero-title">{structure?.subtitle}</h1>
          <p className="hero-description">
            扬子学院六百条 学训、师训、治校办学
          </p>
        </div>
      </section>

      {/* Random Browse Section */}
      <section className="random-section">
        <div className="container">
          <div className="random-section-header">
            <h2 className="random-section-title">
              <Shuffle size={28} />
              随机浏览
            </h2>
            <p className="random-section-subtitle">发现600条的智慧，每一次随机都是新启发</p>
          </div>

          {/* Controls */}
          <div className="random-controls">
            <button
              className="control-btn primary"
              onClick={fetchRandomItem}
              disabled={loading || isAnimating}
            >
              <RefreshCw size={20} className={loading ? 'spinning' : ''} />
              <span>随机一条</span>
            </button>
          </div>

          {/* Main Card */}
          <div className={`random-card-home ${isAnimating ? 'animating' : ''}`}>
            {currentItem && (
              <>
                <div className="random-card-header">
                  <span className="random-number">第 {currentItem.number} 条</span>
                  <div className="random-actions">
                    <button 
                      className={`random-action-btn ${isPlaying ? 'playing' : ''} ${isPaused ? 'paused' : ''}`}
                      onClick={handlePlayAudio}
                      title={getAudioButtonContent().title}
                      disabled={!currentItem}
                    >
                      {getAudioButtonContent().icon}
                    </button>
                    <Link 
                      to={`/item/${currentItem.number}`}
                      className="random-action-btn"
                      title="查看详情"
                    >
                      <Bookmark size={18} />
                    </Link>
                  </div>
                </div>
                
                <div className="random-content">
                  <p className="random-text">{currentItem.content}</p>
                </div>

                <div className="random-progress">
                  <div 
                    className="random-progress-bar"
                    style={{ width: `${(currentItem.number / 600) * 100}%` }}
                  ></div>
                </div>
                <span className="random-progress-text">
                  {currentItem.number} / 600
                </span>
              </>
            )}
          </div>


        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <div className="features-grid">
            {features.map((feature, index) => (
              <Link
                key={feature.id}
                to={`/chapters?part=${feature.id}`}
                className="feature-card animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div
                  className="feature-icon"
                  style={{ backgroundColor: `${feature.color}15`, color: feature.color }}
                >
                  <feature.icon size={32} />
                </div>
                <div className="feature-content">
                  <h3 className="feature-title">{feature.title}</h3>
                  <p className="feature-subtitle">{feature.subtitle}</p>
                  <p className="feature-description">{feature.description}</p>
                  <div className="feature-footer">
                    <span className="feature-count">{feature.count} 条</span>
                    <ChevronRight size={18} className="feature-arrow" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="quick-actions">
        <div className="container">
          <h2 className="section-title">快速开始</h2>
          <div className="actions-grid">
            <Link to="/chapters" className="action-card">
              <BookOpen size={24} />
              <span>浏览章节</span>
            </Link>
            <Link to="/search" className="action-card">
              <span className="action-icon">🔍</span>
              <span>搜索内容</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
